import React, { useState, useRef, useEffect } from 'react'
import { Button, Form, Message, Input, Grid } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import Terms from './Terms'
import i18next from 'i18next'
import { isValidEmail } from 'util/util'
import SupText from 'app/protected/common/SupText'
import PasswordRequirements from 'app/public/PasswordRequirements'

export default function JoinForm(props) {
  const {
    onInput,
    showFormStatus,
    hideFormStatus,
    post,
    ongoing,
    clearForm,
    formMessage,
    formMessageType,
    isFormMessageVisible
  } = props
  const initialState = {
    firstName: '',
    lastName: '',
    email: '',
    password1: '',
    password2: '',
    firstNameError: false,
    lastNameError: false,
    emailError: false,
    password1Error: false,
    password2Error: false,
  }
  const [{
    firstName,
    lastName,
    email,
    password1,
    password2,
    firstNameError,
    lastNameError,
    emailError,
    password1Error,
    password2Error,
  }, setState] = useState(initialState)

  const [modalOpen, setModalOpen] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showPassword2, setShowPassword2] = useState(false)
  const toggleShowPassword = () => {
    event.preventDefault()
    setShowPassword(!showPassword)
  }
  const toggleShowPassword2 = () => {
    event.preventDefault()
    setShowPassword2(!showPassword2)
  }

  const inputChange = (event) => {
    const target = event.target
    const name = target.name
    const value = target.type === 'checkbox' ? target.checked : target.value
    setState(prevState => ({
      ...prevState,
      [name]: value,
      emailError: !isValidEmail(email) || email.length === 0,
      firstNameError: firstName.length === 0,
      lastNameError: lastName.length === 0,
      password1Error: password1.length < 8,
      password2Error: password2.length < 8,
    }))
    onInput()
  }

  const validatePassword = (password) => {
    // At least one alphabetical character
    if (!/[a-zA-Z]/.test(password)) {
      return false
    }
    // At least one number
    if (!/[0-9]/.test(password)) {
      return false
    }
    // At least one special character
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return false
    }
    return true
  }

  const handleSubmit = () => {
    if (email.length === 0 || !isValidEmail(email)) {
      setState(prevState => ({
        ...prevState,
        emailError: true,
      }))
      showFormStatus(i18next.t('email_missing'), 'error')
      return
    }
    if (firstName.length == 0) {
      setState(prevState => ({
        ...prevState,
        firstNameError: true,
      }))
      showFormStatus(i18next.t('first_name_missing'), 'error')
      return
    }
    if (lastName.length == 0) {
      setState(prevState => ({
        ...prevState,
        lastNameError: true,
      }))
      showFormStatus(i18next.t('last_name_missing'), 'error')
      return
    }
    if (password1.length < 8) {
      setState(prevState => ({
        ...prevState,
        password1Error: true,
      }))
      showFormStatus(
        i18next.t('too_short_password_at_least_8_characters_is_required'),
        'error'
      )
      return
    }
    if (password2.length < 8) {
      setState(prevState => ({
        ...prevState,
        password2Error: true,
      }))
      showFormStatus(
        i18next.t('too_short_password_at_least_8_characters_is_required'),
        'error'
      )
      return
    }
    if (password1 != password2) {
      setState(prevState => ({
        ...prevState,
        password1Error: true,
        password2Error: true,
      }))
      showFormStatus(i18next.t('passwords_are_not_matching'), 'error')
      return
    }
    if (!validatePassword(password1)) {
      setState(prevState => ({
        ...prevState,
        password1Error: true,
        password2Error: true,
      }))
      showFormStatus('bad_password', 'error')
      return
    }
    hideFormStatus()
    post({
      data: {
        firstname: firstName,
        lastname: lastName,
        email: email,
        password: password1,
      },
    })
  }

  useEffect(() => {
    if (clearForm) {
      setState(initialState)
    }
  }, [clearForm])

  useEffect(() => {
    if (inputRef) {
      inputRef.current.focus()
    }
  }, [inputRef])

  const getErrorContent = (errorMessage) => {
    console.debug(errorMessage)
    if (errorMessage.startsWith("User already exist")) {
      return i18next.t('user_already_exists')
    } else if (errorMessage === "bad_password") {
      return <>
        <Message.Header>{i18next.t('password_is_too_weak')}</Message.Header>
        <p>{i18next.t('your_password_is_too_easy_to_guess_try_a_new_one')}</p>
        <div>
          {i18next.t('according_to') + ": "}
          <a href="https://haveibeenpwned.com/" target="_blank">{"https://haveibeenpwned.com/"}</a>
        </div>
      </>
    } else if (errorMessage === i18next.t('passwords_are_not_matching')) {
      return i18next.t('passwords_are_not_matching')
    }
    return i18next.t('oh_no_something_went_wrong_try_again')//errorMessage
  }

  const lengthLimit = (minLength, length, showText) => {
    return (
      <Grid>
        <Grid.Row
          style={{ paddingTop: '0px' }}
        >
          <Grid.Column width="10" verticalAlign="middle" textAlign="left">
            {length < minLength ? (
              <div style={{ position: 'relative', top: '10px' }}>
                {showText && <span
                  style={{
                    fontSize: 'x-small',
                    paddingLeft: '2px',
                    color: 'red',
                  }}
                >
                  *{i18next.t('password_requires_at_least_8_characters')}
                </span>}
              </div>
            ) : null}
          </Grid.Column>
          <Grid.Column width="6" verticalAlign="middle" textAlign="right">
            <span
              style={{
                fontSize: 'x-small',
                color: length < minLength ? 'red' : 'green',
                position: 'relative',
                top: '10px',
              }}
            >{`${length}/${minLength}`}</span>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }

  const isSomethingEmpty = () => {
    if (email.length === 0) {
      return true
    } else if (firstName.length === 0) {
      return true
    } else if (lastName.length === 0) {
      return true
    } else if (password1.length === 0) {
      return true
    } else if (password2.length === 0) {
      return true
    } else {
      return false
    }
  }

  const inputRef = useRef()
  const showError = isFormMessageVisible && formMessageType == 'error'
  const showSuccess = isFormMessageVisible && formMessageType == 'success'
  const disableSubmit =
    ongoing ||
    password1.length < 8 ||
    password2.length < 8 ||
    emailError ||
    firstNameError ||
    lastNameError || isSomethingEmpty()

  return (
    <Form
      onSubmit={handleSubmit}
      error={showError}
      success={showSuccess}
    >
      <Form.Field>
        <label>
          {i18next.t('email')}
          {' '}
          <SupText
            text={'*'}
            color={isValidEmail(email) ? 'green' : 'red'}
          />
        </label>
        <Input
          icon="mail"
          iconPosition="left"
          name="email"
          type="email"
          value={email}
          placeholder={i18next.t('your_email')}
          onChange={inputChange}
          error={emailError}
          ref={inputRef}
        />
      </Form.Field>
      <Form.Group widths="equal">
        <Form.Field>
          <label>
            {i18next.t('first_name')}
            {' '}
            <SupText
              text={'*'}
              color={firstName.length > 0 ? 'green' : 'red'}
            />
          </label>
          <Input
            icon="user"
            iconPosition="left"
            name="firstName"
            value={firstName}
            placeholder={i18next.t('first_name')}
            onChange={inputChange}
            error={firstNameError}
          />
        </Form.Field>
        <Form.Field>
          <label>
            {i18next.t('last_name')}
            {' '}
            <SupText
              text={'*'}
              color={lastName.length > 0 ? 'green' : 'red'}
            />
          </label>
          <Input
            icon="user"
            iconPosition="left"
            name="lastName"
            value={lastName}
            placeholder={i18next.t('last_name')}
            onChange={inputChange}
            error={lastNameError}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field>
          <label>{i18next.t('enter_password')}
            {' '}
            <SupText
              text={'*'}
              color={password1.length > 8 ? 'green' : 'red'}
            />
            <span style={{ float: 'right', fontWeight: 300, color: 'gray' }}>*Password needs specific criteria. <a href="#" onClick={() => setModalOpen(true)}>Learn more</a></span>
          </label>
          <Input
            icon="lock"
            iconPosition="left"
            name="password1"
            value={password1}
            type={showPassword ? 'text' : 'password'}
            placeholder={i18next.t('enter_password')}
            onChange={inputChange}
            error={password1Error}
            action={{
              icon: showPassword ? 'eye slash' : 'eye',
              onClick: toggleShowPassword
            }}
          />
          {lengthLimit(8, password1.length, true)}
        </Form.Field>
        <Form.Field>
          <label>
            {i18next.t('verify_password')}
            {' '}
            <SupText
              text={'*'}
              color={password2.length > 8 ? 'green' : 'red'}
            />
          </label>
          <Input
            icon="lock"
            iconPosition="left"
            name="password2"
            value={password2}
            type={showPassword2 ? 'text' : 'password'}
            placeholder={i18next.t('verify_password')}
            onChange={inputChange}
            error={password2Error}
            action={{
              icon: showPassword2 ? 'eye slash' : 'eye',
              onClick: toggleShowPassword2
            }}
          />
          {lengthLimit(8, password2.length)}
        </Form.Field>
      </Form.Group>
      <Terms />
      <Message
        error
        content={getErrorContent(formMessage)}
      />
      <PasswordRequirements
        onClose={() => { setModalOpen(false) }}
        show={modalOpen}
      />
      <Message success content={formMessage} />
      <Button
        type="submit"
        color="green"
        fluid
        primary
        disabled={disableSubmit}
        loading={ongoing}
        style={{ marginTop: '1rem' }}
      >
        {i18next.t('create_account')}
      </Button>
    </Form>
  )

}

JoinForm.propTypes = {
  post: PropTypes.func.isRequired,
  ongoing: PropTypes.bool.isRequired,
  clearForm: PropTypes.bool.isRequired,
  formMessage: PropTypes.string.isRequired,
  showFormStatus: PropTypes.func.isRequired,
  hideFormStatus: PropTypes.func.isRequired,
  formMessageType: PropTypes.string.isRequired,
  isFormMessageVisible: PropTypes.bool.isRequired,
  onInput: PropTypes.func.isRequired,
}
